/**
 * @author michalstolarski
 * @since 2023-1-27
 */

import * as React from "react";
import styled, { css } from "styled-components";

import { TestWrapper } from "@ui/Atoms/TestWrapper";
import {
  SummaryTotal,
  Props as SummaryTotalProps,
} from "@ui/Atoms/SummaryTotal";
import { SummaryDescription } from "@ui/Atoms/SummaryDescription";

interface Props
  extends Omit<
    SummaryTotalProps,
    "variant" | "testIds" | "testId" | "className" | "translations"
  > {
  testId?: string;
  testIds?: {
    price?: string;
    tooltip?: string;
    caption?: string;
    description?: string;
  };
  className?: string;
  description?: string;
  translations: {
    totalText: string;
  };
  isBorderVisible?: boolean;
}

const SidebarSummaryTotal = React.memo((props: Props): React.ReactElement => {
  return (
    <TestWrapper testId={props.testId}>
      <Container
        className={props.className}
        hasBorder={!!props.isBorderVisible}
      >
        <SummaryTotal
          testIds={{
            price: props.testIds?.price,
            tooltip: props.testIds?.tooltip,
            caption: props.testIds?.caption,
          }}
          variant="desktop"
          price={props.price}
          currency={props.currency}
          overridePrice={props.overridePrice}
          caption={props.caption}
          tooltip={props.tooltip}
          translations={props.translations}
          isLoading={props.isLoading}
        />

        {!!props.description && (
          <SummaryDescription
            testId={props.testIds?.description}
            content={props.description}
          />
        )}
      </Container>
    </TestWrapper>
  );
});

SidebarSummaryTotal.displayName = "SidebarSummaryTotal";

export { SidebarSummaryTotal };

const Container = styled.div<{ hasBorder: boolean }>`
  color: ${props => props.theme.palette.grey800};

  ${({ hasBorder }) =>
    hasBorder &&
    css`
      padding-top: ${props => props.theme.margins.base};
      border-top: 1px solid ${props => props.theme.palette.grey100};

      ${props => props.theme.breakpoints.mobile} {
        padding-top: ${props => props.theme.margins.base_x2};
      }
    `}
`;
